// Field normalisers shared by the import script, the CSV import and the UI (spec 03 §1.1).
export const foldText = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

export function trimOrNull(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  return s === "" ? null : s;
}

export function normalizeRegion(raw: unknown): string | null {
  const value = trimOrNull(raw);
  if (!value) return null;
  return ["qc", "quebec"].includes(foldText(value)) ? "QC" : value;
}

export function normalizePostalCode(raw: unknown): string | null {
  const value = trimOrNull(raw)?.toUpperCase() ?? null;
  if (!value) return null;
  const m = /^([A-Z]\d[A-Z])\s*(\d[A-Z]\d)$/.exec(value);
  return m ? `${m[1]} ${m[2]}` : value;
}

export function normalizeWebsite(raw: unknown): string | null {
  const value = trimOrNull(raw);
  if (!value) return null;
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;
}

const EMAIL_RE =
  /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/;

export function normalizeEmail(raw: unknown): {
  email: string | null;
  invalid: boolean;
} {
  const value = trimOrNull(raw)?.toLowerCase() ?? null;
  if (!value) return { email: null, invalid: false };
  return EMAIL_RE.test(value)
    ? { email: value, invalid: false }
    : { email: null, invalid: true };
}

export function splitOwnerName(
  raw: unknown,
): { first_name: string | null; last_name: string } | null {
  const value = trimOrNull(raw);
  if (!value) return null;
  const [first, ...rest] = value.split(/\s+/);
  return rest.length
    ? { first_name: first, last_name: rest.join(" ") }
    : { first_name: null, last_name: first };
}

export function parseFitScore(raw: unknown): number | null {
  if (typeof raw === "number")
    return Number.isFinite(raw) ? Math.trunc(raw) : null;
  const value = trimOrNull(raw);
  return value && /^-?\d+(\.\d+)?$/.test(value)
    ? Math.trunc(Number(value))
    : null;
}

export const parseYes = (raw: unknown): boolean =>
  trimOrNull(raw)?.toLowerCase() === "yes";

export function parseIsoDate(raw: unknown): string | null {
  if (raw instanceof Date)
    return Number.isNaN(raw.getTime()) ? null : raw.toISOString().slice(0, 10);
  const value = trimOrNull(raw);
  return value && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null;
}

export const nameCityKey = (name: unknown, city: unknown): string =>
  `${foldText(trimOrNull(name) ?? "")}|${foldText(trimOrNull(city) ?? "")}`;
